import React from 'react'
import { Head, Link, useForm } from '@inertiajs/react'
import { Container, Typography, Box, Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LocationForm from './Form'

interface GroupRef {
  id: number
  name: string
}

interface GroupOption {
  id: number
  name: string
  company: string
}

interface Location {
  id: number
  slug: string
  name: string
  address: string | null
  latitude: number | null
  longitude: number | null
  phone: string | null
  category: string | null
  description: string | null
  website_url: string | null
  google_place_id: string | null
  groups: GroupRef[]
}

interface Props {
  location: Location
  categories: string[]
  groups: GroupOption[]
}

export default function EditLocation({ location, categories, groups }: Props) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    name: location.name || '',
    address: location.address || '',
    latitude: location.latitude?.toString() || '',
    longitude: location.longitude?.toString() || '',
    phone: location.phone || '',
    category: location.category || '',
    description: location.description || '',
    website_url: location.website_url || '',
    google_place_id: location.google_place_id || '',
    group_ids: location.groups.map((g) => g.id),
  })

  // Inertia's useForm sends a flat body, so wrap it under `location` so Rails
  // strong params (which require :location) receive nested attributes including
  // `group_ids` — `wrap_parameters` only auto-wraps model columns and would
  // strip the has_many :through `group_ids`.
  transform((d) => ({ location: d }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/locations/${location.slug || location.id}`)
  }

  return (
    <>
      <Head title={`Edit ${location.name}`} />
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            component={Link}
            href="/locations"
            startIcon={<ArrowBackIcon />}
          >
            Back to Locations
          </Button>
        </Box>

        <Typography variant="h5" sx={{ mb: 4 }}>Edit Location</Typography>

        <LocationForm
          data={data}
          setData={setData}
          categories={categories}
          groups={groups}
          errors={errors as Record<string, string[]>}
          onSubmit={handleSubmit}
          processing={processing}
          submitLabel="Update Location"
        />
      </Container>
    </>
  )
}
